import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BookOpen } from "lucide-react";
import { PrimaNotaTab } from "../components/ledger/PrimaNotaTab";
import { CausaliTab } from "../components/ledger/CausaliTab";
import { OpenItemsTab } from "../components/ledger/OpenItemsTab";
import { AccountsTab } from "../components/ledger/AccountsTab";
import { TrialBalanceTab } from "../components/ledger/TrialBalanceTab";
import { ExpenseReportsTab } from "../components/ledger/ExpenseReportsTab";
import { VatTab } from "../components/ledger/VatTab";
import { AssetsTab } from "../components/ledger/AssetsTab";
import { BankTab } from "../components/ledger/BankTab";
import { ExportMenu } from "../components/ledger/ExportMenu";
import { ScheduleTab, F24Tab, YearEndTab } from "../components/ledger/FiscalTabs";

const ORDINARY_ENTITY = "srl_it";

export default function Ledger() {
  const { t, entity, currentEntity } = useApp();
  const [accounts, setAccounts] = useState([]);
  const [journal, setJournal] = useState([]);
  const [causali, setCausali] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tick, setTick] = useState(0);
  const ordinary = entity === ORDINARY_ENTITY;

  const load = useCallback(() => {
    api.get(`/accounts?entity_id=${entity}`).then((r) => setAccounts(r.data)).catch(() => {});
    api.get(`/journal?entity_id=${entity}`).then((r) => setJournal(r.data)).catch(() => {});
    api.get(`/causali?entity_id=${entity}`).then((r) => setCausali(r.data)).catch(() => {});
    api.get(`/contacts?entity_id=${entity}`).then((r) => setContacts(r.data)).catch(() => {});
    setTick((x) => x + 1);
  }, [entity]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="ledger-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
          <BookOpen className="h-7 w-7 text-primary" />{t("nav.ledger")}
          {ordinary && <Badge variant="outline" className="text-[10px] uppercase tracking-wide" data-testid="ordinary-badge">{t("ord.badge")}</Badge>}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{ordinary ? t("ord.subtitle") : `${currentEntity?.regime || ""}`}</p>
      </div>
      <ExportMenu />
      </div>
      <Tabs defaultValue="journal">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="journal" data-testid="ledger-tab-journal">{t("ord.tab.journal")}</TabsTrigger>
          {ordinary && <TabsTrigger value="open" data-testid="ledger-tab-open">{t("ord.tab.open")}</TabsTrigger>}
          {ordinary && <TabsTrigger value="expenses" data-testid="ledger-tab-expenses">{t("ord.tab.expenses")}</TabsTrigger>}
          {ordinary && <TabsTrigger value="vat" data-testid="ledger-tab-vat">{t("ord.tab.vat")}</TabsTrigger>}
          {ordinary && <TabsTrigger value="assets" data-testid="ledger-tab-assets">{t("ord.tab.assets")}</TabsTrigger>}
          {ordinary && <TabsTrigger value="bank" data-testid="ledger-tab-bank">{t("ord.tab.bank")}</TabsTrigger>}
          {ordinary && <TabsTrigger value="schedule" data-testid="ledger-tab-schedule">{t("ord.tab.schedule")}</TabsTrigger>}
          {ordinary && <TabsTrigger value="f24" data-testid="ledger-tab-f24">{t("ord.tab.f24")}</TabsTrigger>}
          {ordinary && <TabsTrigger value="yearend" data-testid="ledger-tab-yearend">{t("ord.tab.yearend")}</TabsTrigger>}
          <TabsTrigger value="causali" data-testid="ledger-tab-causali">{t("ord.tab.causali")}</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="ledger-tab-accounts">{t("ord.tab.accounts")}</TabsTrigger>
          <TabsTrigger value="trial" data-testid="ledger-tab-trial">{t("ord.tab.trial")}</TabsTrigger>
        </TabsList>
        <TabsContent value="journal" className="mt-4"><PrimaNotaTab accounts={accounts} causali={causali} contacts={contacts} journal={journal} reload={load} /></TabsContent>
        {ordinary && <TabsContent value="open" className="mt-4"><OpenItemsTab accounts={accounts} onSettled={load} /></TabsContent>}
        {ordinary && <TabsContent value="expenses" className="mt-4"><ExpenseReportsTab accounts={accounts} reload={load} /></TabsContent>}
        {ordinary && <TabsContent value="vat" className="mt-4"><VatTab reload={load} /></TabsContent>}
        {ordinary && <TabsContent value="assets" className="mt-4"><AssetsTab reload={load} /></TabsContent>}
        {ordinary && <TabsContent value="bank" className="mt-4"><BankTab causali={causali} reload={load} /></TabsContent>}
        {ordinary && <TabsContent value="schedule" className="mt-4"><ScheduleTab /></TabsContent>}
        {ordinary && <TabsContent value="f24" className="mt-4"><F24Tab reload={load} /></TabsContent>}
        {ordinary && <TabsContent value="yearend" className="mt-4"><YearEndTab reload={load} /></TabsContent>}
        <TabsContent value="causali" className="mt-4"><CausaliTab accounts={accounts} causali={causali} reload={load} /></TabsContent>
        <TabsContent value="accounts" className="mt-4"><AccountsTab accounts={accounts} reload={load} /></TabsContent>
        <TabsContent value="trial" className="mt-4"><TrialBalanceTab refreshKey={tick} /></TabsContent>
      </Tabs>
    </div>
  );
}
